import { ref, computed } from 'vue'
import { defineStore } from 'pinia'
import { useUserStore } from "@/stores/userStore";
import estimatorService from "@/services/estimatorService";
import customerService from "@/services/customerService";

export const useEstimatorSettingsStore = defineStore('estimator-settings-store', () => {
    
    const loading = ref(false);
    const userStore = useUserStore();
    
    // Image upload constants
    const IMAGE_CONTAINER_NAME = 'customer-estimate-logos';
    const MAX_IMAGES = 5;
    const MAX_FILE_SIZE_MB = 5;
    const ACCEPTABLE_FORMATS = ['image/jpeg', 'image/png'];

    const STANDARD_COST_TYPE_IDS = {
        labor: 1,
        otherCharges: 2
    };

    const TIER_LABELS = {
        1: 'estimator.settings.good',
        2: 'estimator.settings.better',
        3: 'estimator.settings.best'
    };

    const estimateCompanyCostPreferences = ref([]);
    const estimateCompanyCustomCosts = ref([]);
    const estimateCompanyCoverPageImages = ref([]);
    const estimateCompanyLogos = ref([]);
    const estimateCompanySetting = ref({});
    const estimateCompanyTierSettings = ref([]);
    const estimateStandardCosts = ref([]);
    const testimonials = ref([]);
    const testimonialsList = ref([]);

    const customCharges = ref({
        labor: [],
        otherCharges: []
    });

    const percentageInputItems = ref([
        { value: '', name: 'estimator.settings.taxRate' },
        { value: '', name: 'estimator.settings.netProfitGoal' },
        { value: '', name: 'estimator.settings.overhead' }
    ]);

    const goodBetterBestInputsItems = ref([
        { value: '', name: 'estimator.settings.good', tierId: 1 },
        { value: '', name: 'estimator.settings.better', tierId: 2 },
        { value: '', name: 'estimator.settings.best', tierId: 3 }
    ]);

    const customLaborCharges = computed(() => 
        customCharges.value.labor.filter(f => !f.isDeleted)
    );

    const customOtherCharges = computed(() => 
        customCharges.value.otherCharges.filter(f => !f.isDeleted)
    );

    const findCustomField = (type, id) => customCharges.value[type]?.find(f => f.id === id);

    const modifyCustomField = (type, id, modifier) => {
        const field = findCustomField(type, id);
        if (field) modifier(field);
        return field;
    };

    const mapApiToUiField = (apiCost) => ({
        id: apiCost.CustomCostID,
        name: apiCost.Title,
        checked: apiCost.IsActive ?? true,
        error: '',
        saved: true,
        customCostID: apiCost.CustomCostID,
        isDeleted: apiCost.IsDeleted ?? false,
        standardCostTypeID: apiCost.StandardCostTypeID,
        customerCode: apiCost.CustomerCode,
        description: apiCost.Description,
        displayOrder: apiCost.DisplayOrder,
        createdDate: apiCost.CreatedDate,
        modifiedDate: apiCost.ModifiedDate
    });

    const createEmptyField = (type) => {
        const fields = customCharges.value[type];
        const maxOrder = fields.length > 0 
            ? Math.max(...fields.map(f => f.displayOrder || 0)) 
            : 0;

        return {
            id: Date.now(),
            name: '',
            checked: true,
            error: '',
            saved: false,
            customCostID: null,
            isDeleted: false,
            standardCostTypeID: STANDARD_COST_TYPE_IDS[type],
            customerCode: userStore.customerCode,
            description: '',
            displayOrder: maxOrder + 1,
            createdDate: null,
            modifiedDate: null
        };
    };

    // ===== INITIALIZATION ACTIONS =====
    const initializeCustomCostFields = () => {
        const costs = estimateCompanyCustomCosts.value || [];
      
        customCharges.value.labor = costs
            .filter(c => c.StandardCostTypeID === STANDARD_COST_TYPE_IDS.labor)
            .map(mapApiToUiField);
        
        customCharges.value.otherCharges = costs
            .filter(c => c.StandardCostTypeID === STANDARD_COST_TYPE_IDS.otherCharges)
            .map(mapApiToUiField);
    };

    const initializePercentageInputItems = () => {
        const setting = estimateCompanySetting.value;
        percentageInputItems.value = [
            { value: setting.TaxRate ?? '', name: 'estimator.settings.taxRate' },
            { value: setting.NetProfitGoal ?? '', name: 'estimator.settings.netProfitGoal' },
            { value: setting.Overhead ?? '', name: 'estimator.settings.overhead' }
        ];
    };

    const initializeGoodBetterBestItems = () => {
        const tierSettings = estimateCompanyTierSettings.value || [];

        goodBetterBestInputsItems.value = [1, 2, 3].map(tierId => {
            const tier = tierSettings.find(t => t.TierID === tierId);
            return {
                value: tier?.Label || '',
                name: TIER_LABELS[tierId],
                tierId
            };
        });
    };

    // ===== CUSTOM FIELD ACTIONS =====
    const addCustomField = (type) => {
        customCharges.value[type].push(createEmptyField(type));
    };

    const deleteCustomField = (type, id) => {
        const field = findCustomField(type, id);
        if (!field) return;

        if (field.customCostID) {
            field.isDeleted = true;
        } else {
            customCharges.value[type] = customCharges.value[type].filter(f => f.id !== id);
        }
    };

    const saveCustomField = (type, id) => {
        const field = findCustomField(type, id);
        if (!field) return false;

        if (!field.name.trim()) {
            field.error = 'Check the content of the field.';
            return false;
        }

        field.error = '';
        field.saved = true;
        field.description = field.name;
        return true;
    };

    const editCustomField = (type, id) => modifyCustomField(type, id, f => f.saved = false);

    const clearFieldError = (type, id) => modifyCustomField(type, id, f => f.error = '');

    const updateCustomFieldName = (type, id, name) => modifyCustomField(type, id, f => f.name = name);

    const updateCustomFieldChecked = (type, id, checked) => modifyCustomField(type, id, f => f.checked = checked);

    // ===== STANDARD COST PREFERENCE ACTIONS =====
    const toggleStandardCostPreference = (standardCostID, isEnabled) => {
        const existingIndex = estimateCompanyCostPreferences.value.findIndex(
            pref => pref.StandardCostID === standardCostID
        );

        if (existingIndex !== -1) {
            estimateCompanyCostPreferences.value[existingIndex].IsEnabled = isEnabled;
        } else {
            estimateCompanyCostPreferences.value.push({
                CreatedDate: new Date().toISOString(),
                CustomerCode: userStore.customerCode,
                IsEnabled: isEnabled,
                ModifiedDate: new Date().toISOString(),
                StandardCostID: standardCostID,
            });
        }
    };

    const isStandardCostEnabled = (standardCostID) => {
        const preference = estimateCompanyCostPreferences.value.find(
            pref => pref.StandardCostID === standardCostID
        );
        return preference?.IsEnabled ?? true;
    };

    const mapFieldsToPayload = (fields, typeId, customerCode, now) =>
        fields.map((field, index) => ({
            CustomerCode: customerCode,
            StandardCostTypeID: typeId,
            Title: field.name,
            Description: field.name,
            DisplayOrder: index,
            IsActive: field.checked,
            IsDeleted: field.isDeleted || false,
            CustomCostID: field.customCostID,
            CreatedDate: field.createdDate || now,
            ModifiedDate: now
        }));

    const buildImagePayload = (images, customerCode, idField) =>
        images
            .filter(img => img.ImageURI)
            .map((img, index) => ({
                CustomerCode: customerCode,
                [idField]: img[idField] || Math.floor(Math.random() * 1000),
                ImageURI: (img.ImageURI || '').replace(/[ '"]+/g, ''),
                DisplayOrder: index,
                CreatedDate: new Date(),
                ModifiedDate: new Date()
            }));
    // ===== VALIDATION ACTIONS =====
    const validateEstimatorSettings = () => {
        const errors = [];

        // Validate About Us and Terms & Conditions
        const aboutUsContent = estimateCompanySetting.value?.AboutUs || '';
        const termsContent = estimateCompanySetting.value?.TermsAndConditions || '';
        
        if (!aboutUsContent.trim()) {
            errors.push('About Us content is required.');
        }
        if (!termsContent.trim()) {
            errors.push('Terms and Conditions content is required.');
        }

        // Validate custom field names
        const invalidFields = [...customCharges.value.labor, ...customCharges.value.otherCharges]
            .filter(f => !f.saved && !(f.name || '').trim());
        if (invalidFields.length > 0) {
            errors.push('Custom fields must have a name. Please fill in all custom field names or delete empty fields.');
        }

        // Validate percentage inputs
        percentageInputItems.value.forEach((item) => {
            if (item.value !== '' && item.value !== null) {
                const value = parseFloat(item.value);
                if (isNaN(value) || value < 0 || value > 100) {
                    errors.push(`${item.name} must be between 0 and 100.`);
                }
            }
        });

        return {
            isValid: errors.length === 0,
            errors: errors
        };
    };

    // ===== CHANGE TRACKING =====
    let initialState = null;

    const captureInitialState = () => {
        initialState = {
            estimateCompanySetting: JSON.parse(JSON.stringify(estimateCompanySetting.value)),
            customCharges: {
                labor: JSON.parse(JSON.stringify(customCharges.value.labor)),
                otherCharges: JSON.parse(JSON.stringify(customCharges.value.otherCharges))
            },
            percentageInputItems: JSON.parse(JSON.stringify(percentageInputItems.value)),
            goodBetterBestInputsItems: JSON.parse(JSON.stringify(goodBetterBestInputsItems.value)),
            estimateCompanyCostPreferences: JSON.parse(JSON.stringify(estimateCompanyCostPreferences.value))
        };
    };

    const hasChanges = () => {
        if (!initialState) return false;

        return JSON.stringify({
            estimateCompanySetting: estimateCompanySetting.value,
            customCharges: customCharges.value,
            percentageInputItems: percentageInputItems.value,
            goodBetterBestInputsItems: goodBetterBestInputsItems.value,
            estimateCompanyCostPreferences: estimateCompanyCostPreferences.value
        }) !== JSON.stringify(initialState);
    };

    const estimatorSettingsPayload = () => {
        const CustomerCode = userStore.customerCode;
        const now = new Date();
        const [taxRate, netProfitGoal, overhead] = percentageInputItems.value;

        // Build cost preferences from standard costs
        const costPreferencesPayload = estimateCompanyCostPreferences.value.map(pref => ({
            CustomerCode,
            IsEnabled: pref.IsEnabled,
            StandardCostID: pref.StandardCostID,
            CreatedDate: pref.CreatedDate || now,
            ModifiedDate: now
        }));

        // Custom costs payload
        const laborFields = mapFieldsToPayload(
            customCharges.value.labor,
            STANDARD_COST_TYPE_IDS.labor,
            CustomerCode,
            now
        );
        const otherChargesFields = mapFieldsToPayload(
            customCharges.value.otherCharges,
            STANDARD_COST_TYPE_IDS.otherCharges,
            CustomerCode,
            now
        );

        // Build tier settings payload
        const tierSettingsPayload = goodBetterBestInputsItems.value
            .filter(item => item.value !== '')
            .map(item => ({
                CustomerCode,
                TierID: item.tierId,
                Label: item.value,
                CreatedDate: now,
                ModifiedDate: now
            }));

        // Build company setting with all required fields
        const setting = estimateCompanySetting.value;
        const companySettingPayload = {
            CustomerCode,
            TermsAndConditions: setting.TermsAndConditions || '',
            PrimaryBrandColor: setting.PrimaryBrandColor || '#000000',
            SecondaryBrandColor: setting.SecondaryBrandColor || '#000000',
            AboutUs: setting.AboutUs || '',
            TaxRate: taxRate?.value !== '' ? parseInt(taxRate.value) : 0,
            NetProfitGoal: netProfitGoal?.value !== '' ? parseInt(netProfitGoal.value) : 0,
            Overhead: overhead?.value !== '' ? parseInt(overhead.value) : 0,
            CoverImageDescription: setting.CoverImageDescription || '',
            ScopeOfWork: setting.ScopeOfWork || '',
            IncludeManufacturerSellSheets: setting.IncludeManufacturerSellSheets || false,
            ManufacturerSellSheetsMyLibrary: setting.ManufacturerSellSheetsMyLibrary || [],
            ManufacturerSellSheetsSelectedManufacturer: setting.ManufacturerSellSheetsSelectedManufacturer || null
        };

        const coverPageImagesPayload = buildImagePayload(
            estimateCompanyCoverPageImages.value,
            CustomerCode,
            'CompanyCoverPageImageID'
        );
        const logosPayload = buildImagePayload(
            estimateCompanyLogos.value,
            CustomerCode,
            'CompanyLogoID'
        );

        return {
            estimateCompanyCostPreferences: costPreferencesPayload,
            estimateCompanyCustomCosts: [...laborFields, ...otherChargesFields],
            estimateCompanyCoverPageImages: coverPageImagesPayload,
            estimateCompanyLogos: logosPayload,
            estimateCompanySetting: companySettingPayload,
            estimateCompanyTierSettings: tierSettingsPayload
        };
    };

    const getEstimatorSettings = async () => {
        loading.value = true;
        try {
            const res = await estimatorService.getEstimatorSettings();
            if (res.success && res.data) {
                const settings = res.data;
                
                estimateCompanyCostPreferences.value = settings.EstimateCompanyCostPreferences || [];
                estimateCompanyCoverPageImages.value = settings.EstimateCompanyCoverPageImages || [];
                estimateCompanyCustomCosts.value = settings.EstimateCompanyCustomCosts || [];
                estimateCompanyLogos.value = settings.EstimateCompanyLogos || [];
                estimateCompanySetting.value = {
                    ...settings.EstimateCompanySetting || {},
                    AboutUs: settings.EstimateCompanySetting?.AboutUs || '',
                    TermsAndConditions: settings.EstimateCompanySetting?.TermsAndConditions || '',
                    CustomNotes: settings.EstimateCompanySetting?.CustomNotes || ''
                };
                estimateCompanyTierSettings.value = settings.EstimateCompanyTierSettings || [];
                estimateStandardCosts.value = settings.EstimateStandardCosts || [];
                testimonials.value = settings.testimonials || [];

                initializeCustomCostFields();
                initializePercentageInputItems();
                initializeGoodBetterBestItems();
                
                // Capture initial state after data is loaded
                captureInitialState();
            }
        } catch (error) {
            console.error("Error fetching estimator settings:", error);
        } finally {
            loading.value = false;
        }
    };
    getEstimatorSettings();

    const saveEstimatorSettings = async () => {
        loading.value = true;
        try {
            const payload = estimatorSettingsPayload();
            const res = await estimatorService.saveEstimatorSettings(payload);
            
            if (res.success) {
                return { success: true };
            }
            
            console.error("Error saving estimator settings:", res.error);
            return { success: false, error: res.error };
        } catch (error) {
            console.error("Error saving estimator settings:", error);
            return { success: false, error: error.message };
        } finally {
            loading.value = false;
        }
    };

    const getEstimatorTestimonials = async () => {
        loading.value = true;
        try {
            const res = await estimatorService.getEstimatorTestimonials();
            if (res.success) {
                testimonialsList.value = res.data || [];
            } else {
                console.error("Error fetching estimator testimonials:", res.error);
            }
        } catch (error) {
            console.error("Error fetching estimator testimonials:", error);
        } finally {
            loading.value = false;
        }
    };
    getEstimatorTestimonials();

    const updateEstimatorTestimonials = async (payload) => {
        loading.value = true;
        try {
            const res = await estimatorService.updateEstimatorTestimonials(payload);
            if (res.success) {
                getEstimatorTestimonials();
            } else {
                console.error("Error updating estimator testimonials:", res.error);
            }
        } catch (error) {
            console.error("Error updating estimator testimonials:", error);
        } finally {
            loading.value = false;
        }
    };

    const addEstimatorTestimonials = async (payload) => {
        loading.value = true;
        try {
            const res = await estimatorService.updateEstimatorTestimonials(payload);
            if (res.success) {
                getEstimatorTestimonials();
            } else {
                console.error("Error adding estimator testimonials:", res.error);
            }
        } catch (error) {
            console.error("Error adding estimator testimonials:", error);
        } finally {
            loading.value = false;
        }
    };

    // ===== COVER PAGE IMAGES ACTIONS =====
    
    const validateImageFile = (file) => {
        if (!ACCEPTABLE_FORMATS.includes(file.type)) {
            return { valid: false, error: 'Invalid file format. Only .jpg and .png files are allowed.' };
        }
        if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
            return { valid: false, error: `File size exceeds ${MAX_FILE_SIZE_MB}MB limit.` };
        }
        return { valid: true };
    };

    const isDuplicateFileName = (fileName, images) => {
        const sanitizedFileName = fileName.replace(/\s+/g, '').toLowerCase();
        
        return images.some(img => {
            if (!img.ImageURI) return false;
            const imageUriLower = img.ImageURI.toLowerCase();
            return imageUriLower.includes(sanitizedFileName);
        });
    };

    const uploadImageToAzure = async (file) => {
        const sanitizedFileName = file.name.replace(/[ '"]+/g, '');
        const formData = new FormData();
        formData.append('file', file, file.name);

        const result = await customerService.UploadAzureBlobImage(
            formData,
            IMAGE_CONTAINER_NAME,
            sanitizedFileName
        );

        if (result.success && result.data) {
            return { success: true, imageURI: result.data };
        }
        return { success: false, error: result.error || 'Upload failed' };
    };

    const getImageThumbnailUrl = (imageName) => {
        if (!imageName) return '';
        if (imageName.toLowerCase().startsWith('http')) return imageName;
        return customerService.GetAzureBlobImageProxyUrl(IMAGE_CONTAINER_NAME, imageName);
    };

    // ===== IMAGE CONFIGURATION =====
    const IMAGE_TYPES = {
        coverPage: {
            arrayRef: () => estimateCompanyCoverPageImages,
            idField: 'CompanyCoverPageImageID'
        },
        logo: {
            arrayRef: () => estimateCompanyLogos,
            idField: 'CompanyLogoID'
        }
    };

    const addImage = async (file, type) => {
        const config = IMAGE_TYPES[type];
        const imagesArray = config.arrayRef().value;

        // Check max images limit
        if (imagesArray.length >= MAX_IMAGES) {
            return { success: false, error: `Maximum of ${MAX_IMAGES} images allowed.` };
        }

        // Validate file
        const validation = validateImageFile(file);
        if (!validation.valid) {
            return { success: false, error: validation.error };
        }

        // Check for duplicate
        if (isDuplicateFileName(file.name, imagesArray)) {
            return { success: false, error: 'Duplicated image name. We will omit this image.', isDuplicate: true };
        }

        // Upload to Azure
        const uploadResult = await uploadImageToAzure(file);
        if (!uploadResult.success) {
            return { success: false, error: uploadResult.error };
        }

        const newImage = {
            CustomerCode: userStore.customerCode,
            [config.idField]: null,
            ImageURI: uploadResult.imageURI,
            DisplayOrder: imagesArray.length,
            CreatedDate: new Date().toISOString(),
            ModifiedDate: new Date().toISOString(),
            isNewItem: true
        };

        imagesArray.push(newImage);
        return { success: true };
    };

    const removeImage = (index, type) => {
        const config = IMAGE_TYPES[type];
        const imagesArray = config.arrayRef().value;

        if (index >= 0 && index < imagesArray.length) {
            imagesArray.splice(index, 1);
            // Update display order
            imagesArray.forEach((img, idx) => {
                img.DisplayOrder = idx;
            });
            return { success: true };
        }
        return { success: false };
    };

    const addCoverPageImage = (file) => addImage(file, 'coverPage');
    const removeCoverPageImage = (index) => removeImage(index, 'coverPage');
    const addLogoImage = (file) => addImage(file, 'logo');
    const removeLogoImage = (index) => removeImage(index, 'logo');

    const updateCoverImageDescription = (description) => {
        estimateCompanySetting.value.CoverImageDescription = description;
    };

    const coverPageImagesAtLimit = computed(() => estimateCompanyCoverPageImages.value.length >= MAX_IMAGES);
    const logosAtLimit = computed(() => estimateCompanyLogos.value.length >= MAX_IMAGES);
    const coverPageImageCount = computed(() => estimateCompanyCoverPageImages.value.length);
    const logoImageCount = computed(() => estimateCompanyLogos.value.length);

    const deleteEstimatorTestimonial = async (testimonialId) => {
        loading.value = true;
        try {
            const res = await estimatorService.deleteEstimatorTestimonial(testimonialId);
            if (res.success) {
                getEstimatorTestimonials();
            } else {
                console.error("Error deleting estimator testimonial:", res.error);
            }
        } catch (error) {
            console.error("Error deleting estimator testimonial:", error);
        } finally {
            loading.value = false;
        }
    };

    // ===== SETTERS FOR ABOUT US AND TERMS & CONDITIONS =====
    const updateAboutUs = (content) => {
        if (!estimateCompanySetting.value) {
            estimateCompanySetting.value = {};
        }
        estimateCompanySetting.value.AboutUs = content || '';
    };

    const updateTermsAndConditions = (content) => {
        if (!estimateCompanySetting.value) {
            estimateCompanySetting.value = {};
        }
        estimateCompanySetting.value.TermsAndConditions = content || '';
    };

    const updateCustomNotes = (content) => {
        if (!estimateCompanySetting.value) {
            estimateCompanySetting.value = {};
        }
        estimateCompanySetting.value.CustomNotes = content || '';
    };

    return {
        loading,
        estimateCompanyCostPreferences,
        estimateCompanyCoverPageImages,
        estimateCompanyLogos,
        estimateCompanySetting,
        estimateCompanyCustomCosts,
        estimateCompanyTierSettings,
        estimateStandardCosts,
        testimonials,
        testimonialsList,
        getEstimatorSettings,
        percentageInputItems,
        goodBetterBestInputsItems,
        customCharges,
        customLaborCharges,
        customOtherCharges,
        addCustomField,
        deleteCustomField,
        saveCustomField,
        editCustomField,
        clearFieldError,
        updateCustomFieldName,
        updateCustomFieldChecked,
        toggleStandardCostPreference,
        isStandardCostEnabled,
        saveEstimatorSettings,
        updateEstimatorTestimonials,
        addEstimatorTestimonials,
        deleteEstimatorTestimonial,
        addCoverPageImage,
        removeCoverPageImage,
        addLogoImage,
        removeLogoImage,
        updateCoverImageDescription,
        getImageThumbnailUrl,
        coverPageImagesAtLimit,
        logosAtLimit,
        coverPageImageCount,
        logoImageCount,
        MAX_IMAGES,
        MAX_FILE_SIZE_MB,
        ACCEPTABLE_FORMATS,
        validateEstimatorSettings,
        hasChanges,
        captureInitialState,
        updateAboutUs,
        updateTermsAndConditions,
        updateCustomNotes
    };
});
